import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageWrapper } from '@components/layout/page-wrapper';
import { CaretDown } from '@phosphor-icons/react';
import {
  Container,
  Card,
  Title,
  LastUpdated,
  Section,
  Paragraph,
  OrderedList,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
  FAQAnswerInner,
} from './delete-data-page.styles';

/**
 * Data Use Page
 * FAQ-style accordion explaining how user data is used
 */
export const DataUsePage: React.FC = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <PageWrapper contained>
      <Container>
        <Card>
          <Title>How We Use Your Data</Title>
          <LastUpdated>Last updated: October 2026</LastUpdated>

          <Section>
            <Paragraph>
              At The Home CEO, we are committed to transparency about how we collect and use your
              data. This page explains the purposes for which we use your information and how it
              helps us serve you better.
            </Paragraph>
          </Section>

          <FAQContainer>
            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'analytics'}
                onClick={() => toggleFAQ('analytics')}
                aria-expanded={expandedFAQ === 'analytics'}
              >
                How is my data used for analytics?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'analytics'}>
                <FAQAnswerInner>
                  <Paragraph>
                    We use anonymized and aggregated data to understand how our users interact with
                    The Home CEO. This helps us:
                  </Paragraph>
                  <OrderedList>
                    <li>
                      Measure feature usage to understand which tools are most valuable to families
                    </li>
                    <li>Identify technical issues and improve app performance</li>
                    <li>Analyze user flows to streamline the experience</li>
                    <li>Track overall engagement metrics to guide product development</li>
                  </OrderedList>
                  <Paragraph>
                    Analytics data is processed in aggregate and is not used to identify or target
                    individual users. You can opt out of analytics tracking in your profile settings.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'improvements'}
                onClick={() => toggleFAQ('improvements')}
                aria-expanded={expandedFAQ === 'improvements'}
              >
                How does my data help improve the site?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'improvements'}>
                <FAQAnswerInner>
                  <Paragraph>
                    Your usage patterns and feedback help us continuously improve The Home CEO:
                  </Paragraph>
                  <OrderedList>
                    <li>
                      Feature requests and feedback are reviewed to prioritize new development
                    </li>
                    <li>
                      Error reports help us identify and fix bugs quickly
                    </li>
                    <li>
                      Usage patterns inform UI/UX improvements to make the app more intuitive
                    </li>
                    <li>
                      Research tool usage helps us create more relevant and accurate analyses
                    </li>
                  </OrderedList>
                  <Paragraph>
                    We may also use aggregated, de-identified data to train and improve our
                    recommendation algorithms, ensuring better suggestions for all users.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'personalization'}
                onClick={() => toggleFAQ('personalization')}
                aria-expanded={expandedFAQ === 'personalization'}
              >
                How is my data used for personalization?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'personalization'}>
                <FAQAnswerInner>
                  <Paragraph>
                    We use your data to create a personalized experience tailored to your
                    household&apos;s needs:
                  </Paragraph>
                  <OrderedList>
                    <li>
                      Household information (home type, family members, pets) helps us show relevant
                      research tools and recommendations
                    </li>
                    <li>
                      Your location (zip code) enables location-specific insights like local school
                      data and regional cost estimates
                    </li>
                    <li>
                      Your goals and preferences inform which features and content we highlight
                    </li>
                    <li>
                      Past research reports and saved data allow you to pick up where you left off
                    </li>
                  </OrderedList>
                  <Paragraph>
                    Personalization is designed to save you time and surface the most relevant
                    information for your unique situation.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'email'}
                onClick={() => toggleFAQ('email')}
                aria-expanded={expandedFAQ === 'email'}
              >
                How will my email be used?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'email'}>
                <FAQAnswerInner>
                  <Paragraph>
                    Your email address is used for the following purposes:
                  </Paragraph>
                  <OrderedList>
                    <li>
                      <strong>Account creation and authentication:</strong> Your email serves as
                      your unique identifier and login credential
                    </li>
                    <li>
                      <strong>Account notifications:</strong> Important updates about your account,
                      such as password resets, security alerts, and subscription changes
                    </li>
                    <li>
                      <strong>Account updates:</strong> Information about new features, service
                      changes, or updates to our terms and policies
                    </li>
                    <li>
                      <strong>General company contact:</strong> Occasional updates about The Home
                      CEO, including product announcements and company news
                    </li>
                  </OrderedList>
                  <Paragraph>
                    We will never sell your email address to third parties. You can manage your
                    email preferences in your profile settings, including opting out of non-essential
                    communications.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'sms'}
                onClick={() => toggleFAQ('sms')}
                aria-expanded={expandedFAQ === 'sms'}
              >
                How will my phone number be used?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'sms'}>
                <FAQAnswerInner>
                  <Paragraph>
                    If you opt in to text messages, we use your mobile number to send you SMS and MMS
                    messages about your account and The Home CEO. Message and data rates may apply, and
                    you can opt out at any time by replying &quot;STOP&quot;.
                  </Paragraph>
                  <Paragraph>
                    We do not sell or share your SMS opt-in data or personal information with third
                    parties for marketing purposes.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'third-party'}
                onClick={() => toggleFAQ('third-party')}
                aria-expanded={expandedFAQ === 'third-party'}
              >
                Is my data shared with third parties?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'third-party'}>
                <FAQAnswerInner>
                  <Paragraph>
                    We only share data with third parties in limited circumstances:
                  </Paragraph>
                  <OrderedList>
                    <li>
                      <strong>Service providers:</strong> We use trusted services like Firebase
                      (hosting and authentication), Stripe (payments), and analytics tools to
                      operate The Home CEO
                    </li>
                    <li>
                      <strong>Legal requirements:</strong> We may disclose data if required by law
                      or to protect our rights and the safety of our users
                    </li>
                    <li>
                      <strong>Business transfers:</strong> If The Home CEO is acquired or merged,
                      user data may be transferred as part of that transaction
                    </li>
                  </OrderedList>
                  <Paragraph>
                    We do not sell your personal information to advertisers or data brokers. For
                    more details, please review our{' '}
                    <Link to="/legal/privacy-policy">Privacy Policy</Link>.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>

            <FAQItem>
              <FAQQuestion
                $expanded={expandedFAQ === 'control'}
                onClick={() => toggleFAQ('control')}
                aria-expanded={expandedFAQ === 'control'}
              >
                How can I control my data?
                <CaretDown weight="bold" />
              </FAQQuestion>
              <FAQAnswer $expanded={expandedFAQ === 'control'}>
                <FAQAnswerInner>
                  <Paragraph>You have full control over your data:</Paragraph>
                  <OrderedList>
                    <li>
                      <strong>Access:</strong> View all your stored data in your Profile page
                    </li>
                    <li>
                      <strong>Export:</strong> Download a copy of your data at any time from your
                      profile
                    </li>
                    <li>
                      <strong>Update:</strong> Edit your personal information, household details,
                      and preferences
                    </li>
                    <li>
                      <strong>Delete:</strong> Permanently remove your account and all associated
                      data via the <Link to="/legal/delete-data">Delete Data</Link> page
                    </li>
                  </OrderedList>
                  <Paragraph>
                    If you have questions about your data or need assistance, please{' '}
                    <Link to="/legal/contact-us">contact us</Link>.
                  </Paragraph>
                </FAQAnswerInner>
              </FAQAnswer>
            </FAQItem>
          </FAQContainer>
        </Card>
      </Container>
    </PageWrapper>
  );
};
